import * as React from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { locationApi } from '@/lib/api/services/location.api';
import { useAuth } from '@/hooks/useAuth';
import type { Location } from '@/types/location';

const STORAGE_KEY = 'yeschef_active_location_id';

interface ActiveLocationContextValue {
  activeLocation: Location | null;
  setActiveLocation: (location: Location | null) => void;
  locations: Location[];
  isLoading: boolean;
}

const ActiveLocationContext = React.createContext<ActiveLocationContextValue | null>(null);

export function ActiveLocationProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, hasPermission, user } = useAuth();

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['locations', 'all'],
    queryFn: ({ pageParam }) =>
      locationApi.paginate({ limit: 100, page: pageParam as number, isActive: true }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.meta.hasNext ? lastPage.meta.page + 1 : undefined,
    enabled: isAuthenticated && hasPermission('location:read'),
    staleTime: 1000 * 60 * 5,
  });

  const pagesFetched = data?.pages.length ?? 0;

  // Auto-fetch remaining pages, capped at 20 pages (2 000 locations at limit:100)
  React.useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && pagesFetched < 20) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, pagesFetched]);

  const locations = React.useMemo(
    () => data?.pages.flatMap((p) => p.data) ?? [],
    [data],
  );

  const [activeLocationId, setActiveLocationId] = React.useState<number | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? Number(stored) : null;
  });

  const persistActiveLocationId = React.useCallback((nextLocationId: number | null) => {
    setActiveLocationId(nextLocationId);
    if (nextLocationId === null) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    localStorage.setItem(STORAGE_KEY, String(nextLocationId));
  }, []);

  /**
   * Whether `locations` is the whole answer rather than a partial one.
   *
   * Everything below reconciles the stored choice against the list, and every
   * branch of it treats "not in the list" as "not available to you". While the
   * query is still in flight that list is `[]`, which is indistinguishable from
   * an account that genuinely has no locations — so the clear-on-empty branch
   * fired on every fresh mount and wiped the stored id before the real answer
   * arrived. A cook who picked their kitchen was back on an all-locations board
   * after any reload, which mid-service reads as other people's tickets landing
   * on their line rather than as a setting that failed to save.
   *
   * Paging is the same hazard one step in: with more than 100 locations, a
   * stored one on page two is absent from a half-loaded list, and the effect
   * would swap it for a page-one guess. So this waits for the last page too —
   * or for the 20-page cap the fetch effect above stops at.
   */
  const locationsSettled = data !== undefined && (!hasNextPage || pagesFetched >= 20);

  /* eslint-disable react-hooks/set-state-in-effect */
  React.useEffect(() => {
    if (!locationsSettled) return;

    if (!locations.length) {
      if (activeLocationId !== null) {
        persistActiveLocationId(null);
      }
      return;
    }

    if (activeLocationId !== null && locations.some((location) => location.id === activeLocationId)) {
      return;
    }

    const userCompanyId = Number(user?.company?.id);
    const companyScopeIds = new Set<number>();
    for (const scope of user?.scopes ?? []) {
      if (scope.type === 'COMPANY') {
        const parsed = Number(scope.id);
        if (Number.isFinite(parsed) && parsed > 0) companyScopeIds.add(parsed);
      }
    }

    const roleScopeCompanyIds = new Set<number>();
    for (const role of user?.roles ?? []) {
      if (role.scopeType === 'COMPANY') {
        const parsed = Number(role.scopeId);
        if (Number.isFinite(parsed) && parsed > 0) roleScopeCompanyIds.add(parsed);
      }
    }

    const preferredLocation =
      (Number.isFinite(userCompanyId) && userCompanyId > 0
        ? locations.find((location) => location.companyId === userCompanyId)
        : undefined) ??
      (companyScopeIds.size > 0
        ? locations.find((location) => companyScopeIds.has(location.companyId))
        : undefined) ??
      (roleScopeCompanyIds.size > 0
        ? locations.find((location) => roleScopeCompanyIds.has(location.companyId))
        : undefined) ??
      (locations.length === 1 ? locations[0] : null);

    if (preferredLocation) {
      persistActiveLocationId(preferredLocation.id);
      return;
    }

    if (activeLocationId !== null) {
      persistActiveLocationId(null);
    }
  }, [locationsSettled, locations, activeLocationId, persistActiveLocationId, user?.company?.id, user?.scopes, user?.roles]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const activeLocation = locations.find((l) => l.id === activeLocationId) ?? null;

  const setActiveLocation = React.useCallback((location: Location | null) => {
    setActiveLocationId(location?.id ?? null);
    if (location) {
      localStorage.setItem(STORAGE_KEY, String(location.id));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  return (
    <ActiveLocationContext.Provider value={{ activeLocation, setActiveLocation, locations, isLoading }}>
      {children}
    </ActiveLocationContext.Provider>
  );
}

export function useActiveLocation(): ActiveLocationContextValue {
  const ctx = React.useContext(ActiveLocationContext);
  if (!ctx) throw new Error('useActiveLocation must be used within ActiveLocationProvider');
  return ctx;
}
